import { FC, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import {
  Search,
  Play,
  Dumbbell,
  LayoutGrid,
  Video,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IFieldExercise, IFieldExerciseGroup } from '../../../models/field-programming';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import styles from './FieldExercisesTab.module.scss';

function getYoutubeThumbnail(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const regex of patterns) {
    const match = url.match(regex);
    if (match?.[1]) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  }
  return null;
}

const COLOR_PALETTE = [
  { bg: '#effafe', text: '#1a8fb5', border: '#d4eef7' },
  { bg: '#e6f5f4', text: '#1a8f6e', border: '#c8e8e5' },
  { bg: '#fff3e0', text: '#d4860b', border: '#ffe0b2' },
  { bg: '#fce4ec', text: '#c62828', border: '#f8bbd0' },
  { bg: '#f3e5f5', text: '#7b1fa2', border: '#e1bee7' },
  { bg: '#e8eaf6', text: '#3949ab', border: '#c5cae9' },
  { bg: '#e0f2f1', text: '#00796b', border: '#b2dfdb' },
  { bg: '#fff8e1', text: '#f9a825', border: '#ffecb3' },
];

const groupColorMap: Record<string, { bg: string; text: string; border: string }> = {};
let colorIndex = 0;

function getGroupColor(groupId: string) {
  if (!groupColorMap[groupId]) {
    groupColorMap[groupId] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
    colorIndex++;
  }
  return groupColorMap[groupId];
}

const FieldExercisesTab: FC = () => {
  const api = useEndpoints();
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState('');
  const [page, setPage] = useState(1);
  const [selectedExercise, setSelectedExercise] = useState<IFieldExercise | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const groupsQ = useQuery({
    queryKey: ['fieldProgramming', 'exerciseGroups'],
    queryFn: () => api.fieldProgramming.exerciseGroups.list().then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const exercisesQ = useQuery({
    queryKey: ['fieldProgramming', 'exercises', { search: debouncedSearch, groupId, page }],
    queryFn: () =>
      api.fieldProgramming.exercises
        .list({
          search: debouncedSearch || undefined,
          groupId: groupId || undefined,
          page,
          limit: 24,
        })
        .then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const groups = useMemo(() => groupsQ.data ?? [], [groupsQ.data]);
  const exercises = useMemo(() => exercisesQ.data?.docs ?? [], [exercisesQ.data]);
  const totalPages = exercisesQ.data?.totalPages ?? 1;
  const totalDocs = exercisesQ.data?.totalDocs ?? 0;

  const startItem = (page - 1) * 24 + 1;
  const endItem = Math.min(page * 24, totalDocs);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Libreria esercizi campo</h2>
          {!exercisesQ.isLoading && totalDocs > 0 && (
            <span className={styles.headerCount}>{totalDocs}</span>
          )}
        </div>
      </div>

      {/* Search + filters */}
      <div className={styles.filtersCard}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} size={16} />
          <input
            type="text"
            placeholder="Cerca esercizio per nome..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className={styles.searchInput}
          />
        </div>

        {groups.length > 0 && (
          <div className={styles.groupPillsRow}>
            <button
              className={`${styles.groupPill} ${groupId === '' ? styles.groupPillActive : ''}`}
              onClick={() => {
                setGroupId('');
                setPage(1);
              }}
            >
              <LayoutGrid size={13} />
              Tutti
            </button>
            {groups.map((g: IFieldExerciseGroup) => {
              const color = getGroupColor(g._id);
              const isActive = groupId === g._id;
              return (
                <button
                  key={g._id}
                  className={`${styles.groupPill} ${isActive ? styles.groupPillActive : ''}`}
                  style={
                    isActive
                      ? undefined
                      : { background: color.bg, borderColor: color.border, color: color.text }
                  }
                  onClick={() => {
                    setGroupId(g._id);
                    setPage(1);
                  }}
                >
                  {g.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {exercisesQ.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : exercises.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <Search size={24} />
          </div>
          <p className={styles.emptyTitle}>Nessun esercizio trovato</p>
          <p className={styles.emptySubtitle}>
            Il catalogo verrà popolato dall'amministratore o prova a cambiare i filtri
          </p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {exercises.map((ex) => {
              const videoUrl = ex.videoUrl;
              const thumbnail =
                ex.thumbnailUrl || (videoUrl ? getYoutubeThumbnail(videoUrl) : null);
              const groupRef = typeof ex.groupId === 'object' ? ex.groupId : null;
              return (
                <div
                  key={ex._id}
                  className={styles.card}
                  onClick={() => setSelectedExercise(ex)}
                >
                  {thumbnail ? (
                    <div className={styles.cardThumbImg}>
                      <img src={thumbnail} alt="" className={styles.thumbImage} />
                      {videoUrl && (
                        <div className={styles.playOverlay}>
                          <Play size={24} fill="#fff" className={styles.playIcon} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.cardThumb}>
                      {videoUrl ? (
                        <Video size={18} className={styles.thumbIconVideo} />
                      ) : (
                        <Dumbbell size={18} className={styles.thumbIcon} />
                      )}
                    </div>
                  )}

                  <div className={styles.cardBody}>
                    <p className={styles.exerciseName}>{ex.name}</p>

                    {groupRef && (
                      <div className={styles.groupBadges}>
                        <span
                          className={styles.groupBadge}
                          style={(() => {
                            const color = getGroupColor(groupRef._id);
                            return {
                              background: color.bg,
                              color: color.text,
                              borderColor: color.border,
                            };
                          })()}
                        >
                          {groupRef.name}
                        </span>
                      </div>
                    )}

                    {ex.organization && (
                      <p className={styles.organization}>{ex.organization}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                {startItem}–{endItem} di {totalDocs}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>
                  {page} / {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      <GenericModalV2
        isOpen={!!selectedExercise}
        onClose={() => setSelectedExercise(null)}
        title={selectedExercise?.name ?? ''}
        size="lg"
      >
        {selectedExercise && (
          <div className={styles.detailContent}>
            {selectedExercise.videoUrl && (
              <div className={styles.videoWrapper}>
                <iframe
                  src={selectedExercise.videoUrl}
                  title={selectedExercise.name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {typeof selectedExercise.groupId === 'object' && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Categoria</p>
                <div className={styles.groupBadges}>
                  {(() => {
                    const g = selectedExercise.groupId as IFieldExerciseGroup;
                    const color = getGroupColor(g._id);
                    return (
                      <span
                        className={styles.groupBadge}
                        style={{
                          background: color.bg,
                          color: color.text,
                          borderColor: color.border,
                        }}
                      >
                        {g.name}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
            {selectedExercise.organization && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Organizzazione</p>
                <p className={styles.detailDescription}>{selectedExercise.organization}</p>
              </div>
            )}
            {selectedExercise.description && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Descrizione</p>
                <p className={styles.detailDescription}>{selectedExercise.description}</p>
              </div>
            )}
            {selectedExercise.tags?.length > 0 && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Tag</p>
                <div className={styles.groupBadges}>
                  {selectedExercise.tags.map((t) => (
                    <span key={t} className={styles.tagChip}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </GenericModalV2>
    </div>
  );
};

export default FieldExercisesTab;
